import React, { MouseEventHandler } from "react";
import { useTranslation } from 'react-i18next';
import { Destination } from "../common/ObjectTypes";
import { CopyToClipboard, RuleConfiguredIcon, SortingArrows } from "../common/ProjectIcons";
import DeleteButton from "../common/componentstyles/deleteButton";
import DestinationActionsMenu from "../common/DestinationActionsMenu";
import NoResults from "../common/NoResults";
import { HoverTooltip } from "../common/HoverTooltip";
import { tableClasses, buttonClasses, skeletonClasses } from "../common/tailwindClassnames";
import { getDestinationAccount } from "./DestinationHelperFunctions";
import AddDestinationCard from "./AddDestinationCard";
import { toast } from 'react-toastify';

type ConnectionDestinationTableProps = {
    newConnection: boolean,
    destinations: Destination[],
    existingDestPhase0: Destination[],
    existingDestPhase1: Destination[],
    existingDestPhase2: Destination[],
    existingDestPhase3: Destination[],
    addExistingDestinations: boolean,
    updatingDestinations: boolean,
    onAddExistingDestinations: MouseEventHandler,
    onClickAddDestination: Function,
    onSmartAddDestination: Function,
    onViewDetails: Function,
    onRemoveDestination: Function,
    onSelectDestination: Function,
    onOpenRules?: (destination: Destination) => void,
    destinationsWithRules?: Set<string>,
    searchTerm?: string,
    hasAvailableDestinations?: boolean | null,
}

const ConnectionDestinationTable = ({
    newConnection,
    destinations,
    existingDestPhase0,
    existingDestPhase1,
    existingDestPhase2,
    existingDestPhase3,
    addExistingDestinations,
    updatingDestinations,
    onAddExistingDestinations,
    onClickAddDestination,
    onSmartAddDestination,
    onViewDetails,
    onRemoveDestination,
    onSelectDestination,
    onOpenRules,
    destinationsWithRules,
    searchTerm = "",
    hasAvailableDestinations = null,
}: ConnectionDestinationTableProps) => {
    const { t } = useTranslation();
    let destlist = addExistingDestinations ? existingDestPhase0 : destinations;

    // Helper: check if any destination in phase 1, 2, or 3 matches the search term (name or base_url)
    const lowerSearch = searchTerm.trim().toLowerCase();
    function matchesSearch(dest: Destination) {
        return (
            dest.name.toLowerCase().includes(lowerSearch) ||
            dest.base_url.toLowerCase().includes(lowerSearch)
        );
    }
    const hasOtherPhaseResultsInSearch =
        !!lowerSearch && ([...existingDestPhase1, ...existingDestPhase2, ...existingDestPhase3].some(matchesSearch));

    // With no connected destinations yet (connection details context only):
    // the parent opens the picker when the user can see destinations; when they
    // can't, offer the create card; while the check runs, show a skeleton table
    if (!newConnection && destinations.length === 0 && !addExistingDestinations) {
        if (hasAvailableDestinations === false) {
            return (
                <div className="mt-6 flex flex-col items-center">
                    <AddDestinationCard
                        title={t("Create New Destination")}
                        description={t("Set up a brand new destination and connect it.")}
                        onClick={() => onClickAddDestination(false)}
                        dataTestId="CreateNewDestinationCard-button"
                    />
                </div>
            );
        }
        return (
            <table className={tableClasses.container}>
                <tbody className="text-sm transition-all duration-300">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className={tableClasses.row}>
                            <td className={tableClasses.cell}><div className={`h-4 w-4 ${skeletonClasses.base}`} /></td>
                            <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                            <td className={tableClasses.cell}><div className={`h-4 w-40 ${skeletonClasses.base}`} /></td>
                            <td className={`${tableClasses.cell} hidden md:table-cell`}><div className={`h-4 w-28 ${skeletonClasses.base}`} /></td>
                            <td className={`${tableClasses.cell} hidden md:table-cell`}><div className={`h-4 w-24 ${skeletonClasses.base}`} /></td>
                            <td className={`${tableClasses.cell} hidden md:table-cell`}><div className={`h-4 w-24 ${skeletonClasses.base}`} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    return (
        <div>
            <div className="mb-3">
                <label className="text-off-black transition-all duration-300">
                    {addExistingDestinations
                        ? t("Select existing destinations to add to the connection")
                        : `${t("Connected destinations")}: ${destinations.length}`}
                </label>
            </div>
            <table className={tableClasses.container}>
                <thead className={tableClasses.header}>
                    <tr>
                        {addExistingDestinations && <th></th>}
                        <th className={tableClasses.headerCell}>{t("Name")}</th>
                        <th className={`${tableClasses.headerCell} flex items-center gap-2`}>
                            {t("Destination")}
                            <span className={tableClasses.sortIcon}><SortingArrows /></span>
                        </th>
                        <th className={`${tableClasses.headerCell} hidden md:table-cell`}>{t("Workspace")}</th>
                        <th className={`${tableClasses.headerCell} hidden md:table-cell`}>{t("Technology")}</th>
                        <th className={`${tableClasses.headerCell} hidden md:table-cell`}>{t("Account")}</th>
                        {!addExistingDestinations && <th></th>}
                    </tr>
                </thead>

                <tbody className="text-sm transition-all duration-300">
                    {addExistingDestinations && (
                        existingDestPhase3.map((destination: Destination, index: number) => {
                            let dest_url: string | URL;
                            try {
                                dest_url = new URL(destination.base_url).hostname;
                            } catch (err) {
                                dest_url = "";
                            }
                            return (
                                <tr key={destination.id} className={tableClasses.row} data-testid={`ConnectionDestinationTable-existing-row-${destination.id}`}>
                                    <td className={tableClasses.cell}>
                                        <input
                                            className="accent-dark-green cursor-pointer"
                                            type="checkbox"
                                            value={destination.id}
                                            id={destination.id}
                                            name={destination.name}
                                            checked={existingDestPhase2.map((a: any) => a.id).includes(destination.id)}
                                            onChange={() => onSelectDestination(destination)}
                                            data-testid={`ConnectionDestinationTable-existing-checkbox-${destination.id}`}
                                        />
                                    </td>
                                    <td className={tableClasses.cell}>
                                        <div>{destination.name}</div>
                                        <div className="md:hidden mt-1 text-xs text-secondary-gray">
                                            {destination.owner.name} · {destination.type.name}
                                        </div>
                                    </td>
                                    <td className={`${tableClasses.cell} text-blue-basic`}>
                                        <div className="flex items-center gap-2">
                                            <span className="cursor-pointer hover:underline" onClick={() => onViewDetails(destination.id)}>{dest_url}</span>
                                            {destination.base_url && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(destination.base_url);
                                                        toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                            position: toast.POSITION.TOP_RIGHT,
                                                            autoClose: 2000
                                                        });
                                                    }}
                                                    className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                                                    aria-label="Copy URL to clipboard"
                                                >
                                                    <CopyToClipboard />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{destination.owner.name}</td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{destination.type.name}</td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{getDestinationAccount(destination) ?? "—"}</td>
                                </tr>
                            );
                        })
                    )}
                    {addExistingDestinations && updatingDestinations && (
                        Array.from({ length: 5 }).map((_, i) => (
                            <tr key={i} className={tableClasses.row}>
                                <td className={tableClasses.cell}><div className={`h-4 w-4 ${skeletonClasses.base}`} /></td>
                                <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                <td className={tableClasses.cell}><div className={`h-4 w-40 ${skeletonClasses.base}`} /></td>
                                <td className={`${tableClasses.cell} hidden md:table-cell`}><div className={`h-4 w-28 ${skeletonClasses.base}`} /></td>
                                <td className={`${tableClasses.cell} hidden md:table-cell`}><div className={`h-4 w-24 ${skeletonClasses.base}`} /></td>
                                <td className={`${tableClasses.cell} hidden md:table-cell`}><div className={`h-4 w-24 ${skeletonClasses.base}`} /></td>
                            </tr>
                        ))
                    )}
                    {(destlist.length < 1 && !hasOtherPhaseResultsInSearch) ? (
                        <tr>
                            <td colSpan={6} className={`${tableClasses.cell} text-left text-secondary-gray`}>
                                {addExistingDestinations ?
                                    <NoResults
                                        headerMsg={t("No Results Found")}
                                        labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                    />
                                    : t("No Destinations connected to this provider.")}
                            </td>
                        </tr>
                    ) : (
                        (!addExistingDestinations || !updatingDestinations) && destlist.map((destination: Destination) => {
                            let dest_url: string | URL;
                            try {
                                dest_url = new URL(destination.base_url).hostname;
                            } catch (err) {
                                dest_url = "";
                            }
                            return (
                                <tr key={destination.id} className={tableClasses.row} data-testid={addExistingDestinations ? `ConnectionDestinationTable-existing-row-${destination.id}` : undefined}>
                                    {addExistingDestinations && (
                                        <td className={tableClasses.cell}>
                                            <input
                                                className="accent-dark-green cursor-pointer"
                                                type="checkbox"
                                                value={destination.id}
                                                id={destination.id}
                                                name={destination.name}
                                                checked={existingDestPhase1.map((a: any) => a.id).includes(destination.id)}
                                                onChange={() => onSelectDestination(destination)}
                                                data-testid={`ConnectionDestinationTable-existing-checkbox-${destination.id}`}
                                            />
                                        </td>
                                    )}
                                    <td className={tableClasses.cell}>
                                        <div>{destination.name}</div>
                                        <div className="md:hidden mt-1 text-xs text-secondary-gray">
                                            {destination.owner.name} · {destination.type.name}
                                        </div>
                                    </td>
                                    <td className={`${tableClasses.cell} text-blue-basic`}>
                                        <div className="flex items-center gap-2">
                                            <span className="cursor-pointer hover:underline" onClick={() => onViewDetails(destination.id)}>{dest_url}</span>
                                            {destination.base_url && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(destination.base_url);
                                                        toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                            position: toast.POSITION.TOP_RIGHT,
                                                            autoClose: 2000
                                                        });
                                                    }}
                                                    className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                                                    aria-label="Copy URL to clipboard"
                                                >
                                                    <CopyToClipboard />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{destination.owner.name}</td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{destination.type.name}</td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{getDestinationAccount(destination) ?? "—"}</td>
                                    {!addExistingDestinations && (
                                        <td className={`${tableClasses.cell} w-20`}>
                                            <div className="flex flex-row items-center justify-end gap-2">
                                                {destinationsWithRules?.has(destination.id) && (
                                                    <HoverTooltip
                                                        content={t("Rule configured")}
                                                        ariaLabel={t("Rule configured")}
                                                        dataTestId="ConnectionDestinationTable-rule-configured"
                                                    >
                                                        <RuleConfiguredIcon />
                                                    </HoverTooltip>
                                                )}
                                                {onOpenRules ? (
                                                    <DestinationActionsMenu
                                                        destinationName={destination.name}
                                                        onOpenRules={() => onOpenRules(destination)}
                                                        onDelete={() => onRemoveDestination(destination)}
                                                    />
                                                ) : (
                                                    <DeleteButton
                                                        onClick={() => onRemoveDestination(destination)}
                                                        className="m-0"
                                                        ariaLabel="Remove destination"
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })

                    )}
                    {(!newConnection && addExistingDestinations) && (
                        <tr className={tableClasses.row}>
                            <td colSpan={6} className={tableClasses.cell}>
                                <div className="flex flex-row gap-3">
                                    <button onClick={onAddExistingDestinations}
                                        disabled={existingDestPhase1.length === 0 && existingDestPhase2.length === 0}
                                        className={`${buttonClasses.primary} text-base`}>
                                        {t("Add Destinations")}
                                    </button>
                                    {/* With no connected destinations the picker is the tab's only
                                        view, so there is nothing to cancel back to */}
                                    {destinations.length > 0 && (
                                        <button onClick={() => onClickAddDestination(true)}
                                            className={`${buttonClasses.cancel} text-base`}>
                                            {t("Cancel")}
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {addExistingDestinations ? (
                <div className="mt-10 mb-8 flex flex-col items-center gap-4">
                    <p className="text-off-black text-lg transition-colors duration-300">
                        {t("Didn't find the destination you need?")}
                    </p>
                    <AddDestinationCard
                        title={t("Create New Destination")}
                        description={t("Set up a brand new destination and connect it.")}
                        onClick={() => onClickAddDestination(false)}
                        dataTestId="CreateNewDestinationCard-button"
                    />
                </div>
            ) : (
                <div className="mt-10 mb-8 flex flex-col items-center gap-4 transition-all duration-300">
                    <p className="text-off-black text-lg transition-colors duration-300">
                        {t("Need to add another destination?")}
                    </p>
                    <AddDestinationCard
                        title={t("Add Destination")}
                        description={t("Connect an existing destination or set up a new one.")}
                        onClick={() => onSmartAddDestination()}
                        dataTestId="add-destination-button"
                    />
                </div>
            )}
        </div>
    );
};

export default ConnectionDestinationTable;
